import { useEffect, useMemo, useState } from 'react';
import { placesApi } from '../api/places.api';

/**
 * Photos for a proposal card, in two steps (ADR-0005).
 *
 * A card can show several stops, so resolving every photo while it renders
 * would make the bill grow with what is on screen — the pattern ADR-0005
 * exists to stop. Instead:
 *
 * 1. On mount, ask only for what the server already has cached. That request
 *    cannot bill (`resolveCachedPhotoUris` never calls Google), so it is the
 *    same free-on-mount exception `hasCachedDetails` uses for venue details.
 * 2. Anything still missing waits for `reveal()` — one press, one card. That
 *    is the user gesture bounded to a unit that ADR-0005 asks for.
 *
 * The AI sometimes hands back an already-resolved `http(s)` URL instead of a
 * `places/…/photos/…` resource name; those need no lookup at all.
 */

const isResourceName = (v: string): boolean => v.startsWith('places/');

export interface ProposalPhotos {
  /** Raw value (name or URL) → displayable URL, for what is known so far. */
  urls: Record<string, string>;
  /** True while `reveal()` is in flight. */
  revealing: boolean;
  /** Photos that exist but have not been paid for yet. */
  hiddenCount: number;
  reveal: () => void;
}

export function useProposalPhotos(photos: string[], tripId?: string): ProposalPhotos {
  const [resolved, setResolved] = useState<Record<string, string>>({});
  const [revealing, setRevealing] = useState(false);

  // Photos are stable for the life of a proposal, so key the effects on the
  // contents rather than the array identity — the parent rebuilds it on every
  // render and would otherwise re-request forever.
  const key = photos.join('|');
  const names = useMemo(() => photos.filter(isResourceName), [key]);
  const direct = useMemo(
    () => Object.fromEntries(photos.filter(p => !isResourceName(p)).map(p => [p, p])),
    [key],
  );

  useEffect(() => {
    if (names.length === 0) return;
    let alive = true;
    placesApi.cachedPhotos(names)
      .then(urls => { if (alive) setResolved(prev => ({ ...prev, ...urls })); })
      .catch(() => { /* no cached photos is a normal state, not an error */ });
    return () => { alive = false; };
  }, [key]);

  const urls = { ...direct, ...resolved };
  const missing = names.filter(n => !(n in resolved));

  async function reveal() {
    if (missing.length === 0 || revealing) return;
    setRevealing(true);
    const pairs = await Promise.all(missing.map(async name => {
      const url = await placesApi.photo(name, tripId).catch(() => null);
      return [name, url] as const;
    }));
    setResolved(prev => ({
      ...prev,
      ...Object.fromEntries(pairs.filter((p): p is readonly [string, string] => p[1] !== null)),
    }));
    setRevealing(false);
  }

  return { urls, revealing, hiddenCount: missing.length, reveal };
}
